/**
 * Google Ads Conversion Tracking — unified dashboard card.
 *
 * Single surface that houses both:
 * - the online (tag-based) conversion setup wizard — always visible
 * - the offline (Data Manager API) upload modal — only for members in
 *   `OFFLINE_CONVERSIONS_BETA_MEMBER_IDS`
 *
 * Replaces the previous standalone `ConversionSetupPrompt` and
 * `OfflineConversionUploadPrompt` cards that sat as siblings on the Home
 * dashboard. Future surfaces (e.g., Matomo bridge per
 * `docs/MATOMO_GOOGLE_ADS_BRIDGE_PLAN.md`) slot in as additional sections
 * below the divider without touching the mount site.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useOfflineConversionsAccess } from '@/hooks/useOfflineConversionsAccess';
import { ConversionSetupWizard } from './ConversionSetupWizard';
import { OfflineConversionUploadModal } from '../OfflineConversionUpload/OfflineConversionUploadModal';
import { PlatformBadge } from '@/components/features/CampaignCard/shared/PlatformBadge';

interface GoogleConversionTrackingCardProps {
  googleAdsAccountId: string;
}

export const GoogleConversionTrackingCard: React.FC<GoogleConversionTrackingCardProps> = ({
  googleAdsAccountId,
}) => {
  const [isOnlineWizardOpen, setIsOnlineWizardOpen] = useState(false);
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);
  const hasOfflineAccess = useOfflineConversionsAccess();

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-6 pt-4 pb-6">
        {/* Shared card header — brand badge + title */}
        <div className="flex items-center gap-2 mb-4">
          <PlatformBadge platform="Google Ads" />
          <h3 className="text-lg font-semibold text-gray-900">
            Google Ads Conversion Tracking
          </h3>
        </div>

        {/* Online section — always rendered */}
        <section>
          <p className="text-sm text-gray-600">
            Track purchases, leads, and sign-ups from your ads. This helps
            optimize your campaigns for better results.
          </p>
          <div className="mt-4">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsOnlineWizardOpen(true)}
            >
              Set Up Tracking
            </Button>
          </div>
        </section>

        {/* Offline section — only for allowlisted members */}
        {hasOfflineAccess && (
          <>
            <hr className="my-6 border-gray-200" />
            <section>
              <h4 className="text-base font-semibold text-gray-900">
                Import offline conversions
              </h4>
              <p className="mt-1 text-sm text-gray-600">
                Upload conversions from your CRM via the Data Manager API.
                Hashed-identifier matching for Enhanced Conversions for Leads.
              </p>
              <div className="mt-4">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsOfflineModalOpen(true)}
                >
                  Start an Upload
                </Button>
              </div>
            </section>
          </>
        )}
      </div>

      <ConversionSetupWizard
        isOpen={isOnlineWizardOpen}
        onClose={() => setIsOnlineWizardOpen(false)}
        googleAdsAccountId={googleAdsAccountId}
      />
      {/* Defense-in-depth: even though the CTA is gated, also gate the modal
          render in case a stale state somehow flipped `isOfflineModalOpen`. */}
      {hasOfflineAccess && isOfflineModalOpen && (
        <OfflineConversionUploadModal
          googleAdsAccountId={googleAdsAccountId}
          onClose={() => setIsOfflineModalOpen(false)}
        />
      )}
    </>
  );
};
